// src/components/comments/CommentList.jsx
//
// Renders the comment "modules" from the collaborator design via CommentCard,
// plus each card's reply thread. Owns the action wiring (notify / discard /
// edit / tag / report / mark-unread) and the notify picker.
//
// The thread below a card shows existing replies only. Writing one happens in
// the box on the card itself, which is always open -- so the "Replies" toggle
// is now purely for reading back, and collapsing it no longer takes the reply
// box away with it.
//
// Mutations are optimistic where the result is obvious to the user (discard
// removes the card, edit swaps the text) and rolled back on failure, so the
// panel never shows state the server didn't accept.
import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import '../../styles/CommentSidebar.css';
import CommentCard from './CommentCard';
import NotifyModal from './NotifyModal';
import { fetchReplies, postReply } from '../../utils/api';
import { commentApi } from '../../utils/readerApi';

export default function CommentList({
  comments,
  bookId,
  itemType = 'book',
  currentUserId,
  // Passed straight through to each card, which gates notify / discard / tags
  // on them. Named for what they are rather than for what they unlock, since
  // the three actions do not unlock together -- see CommentCard.
  isAdmin = false,
  isItemOwner = false,
  collaborators = [],
  onChanged,
  // Two cards per row: the editor's Collab panel. Each card keeps its replies
  // in its own column. The reader's sidebar leaves this off.
  twoUp = false,
}) {
  const [openThreads, setOpenThreads] = useState({});
  const [repliesByParent, setRepliesByParent] = useState({});
  const [replyBusy, setReplyBusy] = useState({});
  const [busy, setBusy] = useState({});
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [toast, setToast] = useState('');

  const setRowBusy = (id, v) => setBusy((s) => ({ ...s, [id]: v }));

  const handleToggleThread = useCallback(
    async (bid, c) => {
      const id = c.id || c._id;
      const next = !openThreads[id];
      setOpenThreads((s) => ({ ...s, [id]: next }));
      if (next && !repliesByParent[id]) {
        try {
          const res = await fetchReplies(bid, id, itemType);
          const items = Array.isArray(res) ? res : (res.items || []);
          setRepliesByParent((s) => ({ ...s, [id]: items }));
        } catch (e) {
          console.error('fetchReplies failed', e);
        }
      }
    },
    [openThreads, repliesByParent, itemType]
  );

  const handleSubmitReply = useCallback(
    async (bid, parentId, text) => {
      if (replyBusy[parentId]) return;
      setReplyBusy((s) => ({ ...s, [parentId]: true }));
      try {
        const created = await postReply(bid, { text, parentId }, itemType);
        setRepliesByParent((s) => ({ ...s, [parentId]: [...(s[parentId] || []), created] }));
        setOpenThreads((s) => ({ ...s, [parentId]: true }));
      } catch (e) {
        console.error('postReply failed', e);
      } finally {
        setReplyBusy((s) => ({ ...s, [parentId]: false }));
      }
    },
    [replyBusy, itemType]
  );

  const handleDiscard = async (id) => {
    setRowBusy(id, true);
    try {
      await commentApi.discard(id);
      // The server drops discarded comments from the list, so a refresh is
      // what actually removes the card.
      onChanged?.();
    } catch (e) {
      setToast(e?.message || 'Could not discard that comment.');
    } finally {
      setRowBusy(id, false);
    }
  };

  const handleEdit = async (id, text) => {
    setRowBusy(id, true);
    try {
      await commentApi.update(id, { text });
      onChanged?.();
    } catch (e) {
      setToast(e?.message || 'Could not save your edit.');
    } finally {
      setRowBusy(id, false);
    }
  };

  const handleAddTags = async (id, tags) => {
    setRowBusy(id, true);
    try {
      await commentApi.update(id, { tags });
      onChanged?.();
    } catch (e) {
      setToast(e?.message || 'Could not update tags.');
    } finally {
      setRowBusy(id, false);
    }
  };

  const handleMarkUnread = async (id) => {
    setRowBusy(id, true);
    try {
      await commentApi.markUnread(id);
      onChanged?.();
    } catch (e) {
      setToast(e?.message || 'Could not mark as unread.');
    } finally {
      setRowBusy(id, false);
    }
  };

  const handleReport = async (id) => {
    setRowBusy(id, true);
    try {
      await commentApi.report(id, '');
      setToast('Reported to ReVisionari. Thank you.');
    } catch (e) {
      // 409 means this user already reported it — say so plainly rather than
      // presenting it as a failure.
      const msg = /already reported/i.test(e?.message || '')
        ? 'You have already reported this comment.'
        : (e?.message || 'Could not send that report.');
      setToast(msg);
    } finally {
      setRowBusy(id, false);
    }
  };

  const handleNotify = async (userIds) => {
    const id = notifyTarget?.id || notifyTarget?._id;
    if (!id) return;
    const res = await commentApi.notify(id, userIds);
    const skipped = res?.skipped?.length || 0;
    setToast(
      skipped
        ? `Notified ${res.notified.length}. ${skipped} had already been notified.`
        : `Notified ${res.notified.length}.`
    );
    onChanged?.();
  };

  if (!comments || comments.length === 0) {
    return <p className="empty-comment">No comments yet.</p>;
  }

  return (
    <Box
      className="comment-list"
      sx={twoUp ? {
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 1.25, alignItems: 'start',
      } : undefined}
    >
      {toast && (
        <Box
          onClick={() => setToast('')}
          sx={{
            fontSize: 12, color: '#1A1A1A', backgroundColor: '#F5D76E',
            borderRadius: '6px', px: 1.5, py: 1, mb: 1.5, cursor: 'pointer',
            gridColumn: '1 / -1',
          }}
        >
          {toast}
        </Box>
      )}

      {comments.map((c) => {
        const cid = c.id || c._id;
        const open = !!openThreads[cid];
        const replies = repliesByParent[cid] || [];

        return (
          <div className="comment-item" key={cid} style={{ minWidth: 0 }}>
            <CommentCard
              comment={c}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              isItemOwner={isItemOwner}
              busy={!!busy[cid]}
              replyBusy={!!replyBusy[cid]}
              onNotify={(target) => setNotifyTarget(target)}
              onDiscard={handleDiscard}
              onEdit={handleEdit}
              onAddTags={handleAddTags}
              onMarkUnread={handleMarkUnread}
              onReport={handleReport}
              onReply={(id, text) => handleSubmitReply(bookId, id, text)}
            />

            <Box sx={{ mt: -0.5, mb: 1.5, pl: 0.5 }}>
              <Box
                component="button"
                type="button"
                onClick={() => handleToggleThread(bookId, c)}
                sx={{
                  font: 'inherit', fontSize: 11, color: '#FFFFFF',
                  background: 'none', border: 'none', cursor: 'pointer', p: 0,
                  textDecoration: 'underline',
                }}
              >
                {c.reply_count > 0
                  ? `${open ? 'Hide' : 'Show'} ${c.reply_count} ${c.reply_count === 1 ? 'reply' : 'replies'}`
                  : (open ? 'Hide replies' : 'Replies')}
              </Box>
            </Box>

            {open && (
              <div className="comment-thread" style={{ marginTop: 4, marginBottom: 14, paddingLeft: 14, borderLeft: '2px solid rgba(255,255,255,0.25)' }}>
                {replies.map((r) => {
                  const rid = r.id || r._id;
                  const who = r.user?.username || r.user?.name || r.user?.email || 'User';
                  return (
                    <Box key={rid} sx={{ my: 1 }}>
                      <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                        {who}
                        {r.createdAt ? ` · ${new Date(r.createdAt).toLocaleString()}` : ''}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: '#FFFFFF', mt: 0.25 }}>
                        {r.text}
                      </Typography>
                    </Box>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <NotifyModal
        open={Boolean(notifyTarget)}
        comment={notifyTarget}
        candidates={collaborators}
        onClose={() => setNotifyTarget(null)}
        onSubmit={handleNotify}
      />
    </Box>
  );
}
